// src/pages/AddMedia.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar8 from "../components/Navbar8";
import ProvideImage from "../components/ProvideImage";
import ProvideVideo from "../components/ProvideVideo";
import { getCurrentUserId } from "../services/database";
import { saveImageMappings, updateHtmlWithImages } from "../services/imageService";
import { saveVideoMappings, updateHtmlWithVideos } from "../services/videoService";
import DesktopWarningBanner from "../components/DesktopWarningBanner";
import ScrollIndicator from "../components/ScrollIndicator";

const injectScrollbarStyles = (htmlContent) => {
  // First, escape any unescaped ampersands in the HTML content
  //const escapedHtml = htmlContent.replace(/&(?![a-zA-Z0-9#]+;)/g, '&amp;');
  
  const injection = `
    <style>
      /* scrollbar styling */
      ::-webkit-scrollbar { width: 6px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb { background-color: rgba(100,100,100,0.5); border-radius: 4px; }
      ::-webkit-scrollbar-thumb:hover { background-color: rgba(100,100,100,0.7); }
      * { scrollbar-width: thin; scrollbar-color: rgba(100,100,100,0.5) transparent; }

      /* Fixed selectors - only highlight actual media elements with correct data-id */
      /* highlight image placeholders - FIXED to require data-id="image" */
      [id^="image-"][data-id="image"] {
        position: relative !important;
        border: 3px solid #facc15 !important;
      }
      [id^="image-"][data-id="image"]::after {
        content: attr(data-number);
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #facc15;
        color: #000;
        font-weight: 700;
        border-radius: 9999px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        z-index: 9999;
      }

      /* highlight video placeholders - FIXED to require data-id="video" */
      [id^="video-"][data-id="video"] {
        position: relative !important;
        border: 3px solid #ef4444 !important;
      }
      [id^="video-"][data-id="video"]::after {
        content: attr(data-number);
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #ef4444;
        color: #fff;
        font-weight: 700;
        border-radius: 9999px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        z-index: 9999;
      }
    </style>

    <script>
      // Function to run when DOM is ready
      function addNumberBadges() {
        console.log("Adding number badges to media elements");
        
        // Only select elements with BOTH id starting with "image-" AND data-id="image"
        const imageElements = document.querySelectorAll('[id^="image-"][data-id="image"]');
        
        // Only select elements with BOTH id starting with "video-" AND data-id="video"
        const videoElements = document.querySelectorAll('[id^="video-"][data-id="video"]');
        
        console.log("Found image elements:", imageElements.length);
        console.log("Found video elements:", videoElements.length);
        
        // Process image elements
        imageElements.forEach(el => {
          const id = el.id;
          const match = id.match(/[0-9]+/);
          if (match) {
            el.setAttribute('data-number', match[0]);
            console.log('Added data-number', match[0], 'to element', id);
          }
        });
        
        // Process video elements
        videoElements.forEach(el => {
          const id = el.id;
          const match = id.match(/[0-9]+/);
          if (match) {
            el.setAttribute('data-number', match[0]);
            console.log('Added data-number', match[0], 'to element', id);
          }
        });
      }
      
      // Run immediately
      addNumberBadges();
      
      // Also run when DOM is fully loaded
      document.addEventListener('DOMContentLoaded', addNumberBadges);
      
      // Set up MutationObserver to detect DOM changes
      if (typeof MutationObserver !== 'undefined') {
        const observer = new MutationObserver(mutations => {
          addNumberBadges();
        });
        
        // Start observing once DOM is ready
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            observer.observe(document.body, {
              childList: true,
              subtree: true,
              attributes: false
            });
          });
        } else {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false
          });
        }
      }
      
      // Run a few times with delay to ensure everything loads
      setTimeout(addNumberBadges, 100);
      setTimeout(addNumberBadges, 500);
    </script>`;

  if (htmlContent.includes('<head>')) {
    return htmlContent.replace('<head>', '<head>' + injection);
  }

  return htmlContent.replace('<html>', '<html><head>' + injection + '</head>');
};
/* ---------- rate‑limit reporter (identical to ViewDetails) ---------- */
const injectRateLimitReporter = (htmlContent) => {
  const reporterScript = `
    <script>
      (function(){
        const API_MARKER = '/api/v1/images/';
        window.addEventListener('error', function(e){
          const img = e.target;
          if (img.tagName === 'IMG' && img.src.includes(API_MARKER)) {
            fetch(img.src, { method: 'HEAD', cache: 'no-store' })
              .then(r => {
                if (r.status === 429) {
                  parent.postMessage(
                    { type: 'IMAGE_RATE_LIMIT', src: img.src },
                    '*'
                  );
                }
              })
              .catch(()=>{});
          }
        }, true);
      })();
    </script>
  `;
  return htmlContent.includes('</body>')
    ? htmlContent.replace('</body>', reporterScript + '</body>')
    : htmlContent + reporterScript;
};


const AddMedia = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { version, html, device, projectId, viewId, currentVersionIndex } = location.state || {};

  const [showProvideImageModal, setShowProvideImageModal] = useState(false);
  const [showProvideVideoModal, setShowProvideVideoModal] = useState(false);
  const [currentMediaId, setCurrentMediaId] = useState(null);
  const [uploadedImages, setUploadedImages] = useState({});
  const [uploadedVideos, setUploadedVideos] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modifiedHtml, setModifiedHtml] = useState(null);
  const [userId, setUserId] = useState(null);

  // Initialize with existing uploaded media if any
  useEffect(() => {
    if (version) {
      // For images
      const normalizedImages = version.imageMappings
        ? Object.fromEntries(
            Object.entries(version.imageMappings).map(([ph, id]) => [ph, { imageId: id }])
          )
        : (version.uploadedImages || {});

      setUploadedImages(normalizedImages);

      // For videos - similar approach
      const normalizedVideos = version.videoMappings
        ? Object.fromEntries(
            Object.entries(version.videoMappings).map(([ph, data]) => [ph, { 
              sourceUrl: typeof data === 'string' ? data : data.sourceUrl,
              embedUrl: typeof data === 'string' ? null : data.embedUrl, 
              platform: typeof data === 'string' ? null : data.platform
            }])
          )
        : (version.uploadedVideos || {});

      setUploadedVideos(normalizedVideos);
    }

    const bootstrapUserId = async () => {
      try {
        const uid = await getCurrentUserId();
        setUserId(uid);
      } catch (err) {
        console.error("Error fetching userId", err);
      }
    };

    bootstrapUserId();
  }, [version]);

  // Update the HTML preview when uploadedImages or uploadedVideos change
  useEffect(() => {
    if ((Object.keys(uploadedImages).length > 0 || Object.keys(uploadedVideos).length > 0) && 
        (version?.html || html) && userId) {
      
      // First update with images
      let updatedHtml = updateHtmlWithImages(
        version?.html || html,
        uploadedImages,
        userId
      );
      
      // Then update with videos if we have any
      if (Object.keys(uploadedVideos).length > 0) {
        updatedHtml = updateHtmlWithVideos(updatedHtml, uploadedVideos);
      }
      
      setModifiedHtml(updatedHtml);
    }
  }, [uploadedImages, uploadedVideos, version, html, userId]);

  // Validate required props
  useEffect(() => {
    if (!viewId || !version?.versionId) {
      console.warn("AddMedia is missing required viewId or versionId props");
    }
  }, [viewId, version]);

  if (!version) return <div className="p-10">Missing data…</div>;

  const container =
    device === "Mobile"
      ? "border rounded-md shadow-sm p-4 bg-white mx-auto max-w-sm"
      : "border rounded-md shadow-sm p-4 bg-white w-full";

  // Helper to build card color
  const badge = (id, type) => (
    <span
      className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold mr-2 ${
        type === "image" ? "bg-yellow-400 text-black" : "bg-red-500 text-white"
      }`}
    >
      {id.split("-")[1]}
    </span>
  );

  const handleOpenProvideImage = (imageId) => {
    setCurrentMediaId(imageId);
    setShowProvideImageModal(true);
  };

  const handleOpenProvideVideo = (videoId) => {
    setCurrentMediaId(videoId);
    setShowProvideVideoModal(true);
  };

  const handleImageSelect = async (image) => {
    try {
      setLoading(true);
      // Update the uploadedImages state with the selected image
      setUploadedImages((prev) => ({
        ...prev,
        [currentMediaId]: {
          imageId: image.id || image._id,
          fileName: image.fileName
        }
      }));
      
      setShowProvideImageModal(false);
    } catch (error) {
      console.error('Error selecting image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoSelect = (data) => {
    try {
      setLoading(true);
      // Update the uploadedVideos state with the validated video data
      setUploadedVideos((prev) => ({
        ...prev,
        [currentMediaId]: data // {embedUrl, platform}
      }));
      
      setShowProvideVideoModal(false);
    } catch (error) {
      console.error('Error selecting video:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (saving || !userId) return; // prevent double clicks or save without userId
    setSaving(true);
    
    try {
      if (!viewId || !version.versionId) {
        throw new Error("Missing viewId or versionId");
      }
      
      // Save image mappings if there are any
      if (Object.keys(uploadedImages).length > 0) {
        await saveImageMappings(
          viewId,
          version.versionId,
          uploadedImages,
          userId
        );
      }
      
      // Save video mappings if there are any
      if (Object.keys(uploadedVideos).length > 0) {
        await saveVideoMappings(
          viewId,
          version.versionId,
          uploadedVideos,
          userId
        );
      }
      
      // Return to the view details page with the version index preserved
      navigate('/view-details', {
        state: {
          projectId,
          viewId,
          currentVersionIndex,
          uploadedImages,
          uploadedVideos
        }
      });
    } catch (error) {
      console.error('Error saving media:', error);
      alert("Failed to save changes: " + (error.message || "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    // Return to the view details page with the version index preserved
    navigate('/view-details', {
      state: {
        projectId,
        viewId,
        currentVersionIndex
      }
    });
  };

  return (
    <div className="relative min-h-screen bg-gray-50">
      <DesktopWarningBanner isDesktop={device === "Desktop"} />
      <Navbar8 
        onClose={handleClose} 
        onSave={handleSave}
        isSaving={saving || !userId}
      />

      <div className="max-w-8xl mx-auto p-4 space-y-6">
        {/* HTML preview */}
        <div className={`${container} overflow-y-auto`} style={{ height: "75vh" }}>
          <iframe
            title="Add media preview"
            
            srcDoc={
              injectRateLimitReporter(
              injectScrollbarStyles(modifiedHtml || version.html || html)
              )
            }

            style={{ width: "100%", height: "100%", border: "none" }}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        </div>

        {/* Media cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Images */}
          {Array.isArray(version.images) &&
            version.images.map((img) => (
              <div key={img.id} className="p-4 border rounded-lg bg-white shadow-sm flex items-center justify-between">
                <div className="flex items-center">
                  {badge(img.id, "image")}
                  <div>
                    <p className="text-sm font-medium">Image {img.id.split("-")[1]}</p>
                    <p className="text-xs text-gray-500">
                      Suggested: {img.aspectRatio} • {img.recommendedSize}
                    </p>
                  </div>
                </div>
                <button 
                  className="px-3 py-1 bg-black hover:bg-gray-800 text-white text-xs rounded"
                  onClick={() => handleOpenProvideImage(img.id)}
                  disabled={loading && currentMediaId === img.id}
                >
                  {loading && currentMediaId === img.id ? "Loading..." : "Provide Image"}
                </button>
              </div>
            ))}

          {/* Videos */}
          {Array.isArray(version.videos) &&
            version.videos.map((vid) => (
              <div key={vid.id} className="p-4 border rounded-lg bg-white shadow-sm flex items-center justify-between">
                <div className="flex items-center">
                  {badge(vid.id, "video")}
                  <div>
                    <p className="text-sm font-medium">Video {vid.id.split("-")[1]}</p>
                    <p className="text-xs text-gray-500">{vid.aspectRatio}</p>
                  </div>
                </div>
                <button 
                  className="px-3 py-1 bg-black hover:bg-gray-800 text-white text-xs rounded"
                  onClick={() => handleOpenProvideVideo(vid.id)}
                  disabled={loading && currentMediaId === vid.id}
                >
                  {loading && currentMediaId === vid.id ? "Loading..." : "Provide Video"}
                </button>
              </div>
            ))}
        </div>
      </div>

      {/* Provide Image Modal */}
      {showProvideImageModal && (
        <ProvideImage
          onClose={() => setShowProvideImageModal(false)}
          onSelect={handleImageSelect}
          projectId={projectId}
          imageId={currentMediaId}
        />
      )}

      {/* Provide Video Modal */}
      {showProvideVideoModal && (
        <ProvideVideo
          onClose={() => setShowProvideVideoModal(false)}
          onSelect={handleVideoSelect}
          videoId={currentMediaId}
        />
      )}
       <ScrollIndicator /> 
    </div>
  );
};

export default AddMedia;